import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from '../styles/components/_Modal.module.scss';
import { gsap } from 'gsap';

const Modal = ({ isOpen, onClose, children }) => {
  const modalRef = useRef(null);
  const overlayRef = useRef(null);
  const contentRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const scrollPosition = useRef(0);

  const lockScroll = () => {
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.overflow = 'hidden';
    scrollPosition.current = scrollY;
  };

  const unlockScroll = () => {
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.top = '';
    document.body.style.overflow = '';
    window.scrollTo(0, scrollPosition.current);
  };

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      lockScroll();
      
      // Petit délai pour s'assurer que le DOM est prêt
      requestAnimationFrame(() => {
        if (modalRef.current && overlayRef.current) {
          gsap.set(modalRef.current, { x: '100%' });
          gsap.set(overlayRef.current, { autoAlpha: 0 });
          
          const tl = gsap.timeline();
          tl.to(overlayRef.current, { autoAlpha: 1, duration: 0.3 })
            .to(modalRef.current, { 
              x: '0%', 
              duration: 0.3, 
              ease: 'power2.out'
            }, '-=0.2');
        }
      });
    } else {
      if (modalRef.current && overlayRef.current) {
        const tl = gsap.timeline({
          onComplete: () => {
            setIsVisible(false);
            unlockScroll();
          }
        });
        
        tl.to(modalRef.current, {
          x: '100%',
          duration: 0.3,
          ease: 'power2.in'
        })
        .to(overlayRef.current, {
          autoAlpha: 0,
          duration: 0.3
        }, '-=0.2');
      }
    }

    return () => {
      if (isVisible) {
        unlockScroll();
      }
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    setStartX(e.touches[0].clientX);
    setCurrentX(e.touches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || e.touches.length !== 1) return;
    const touchX = e.touches[0].clientX;
    setCurrentX(touchX);
    
    if (modalRef.current) {
      const deltaX = touchX - startX;
      if (deltaX > 0) {
        gsap.set(modalRef.current, { x: deltaX });
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    const deltaX = currentX - startX;
    if (deltaX > 50) {
      onClose();
    } else {
      gsap.to(modalRef.current, {
        x: 0,
        duration: 0.3,
        ease: 'power2.out'
      });
    }
    setIsDragging(false);
    setStartX(0);
    setCurrentX(0);
  };

  if (!isVisible) return null;

  return createPortal(
    <div ref={overlayRef} className={styles.modalOverlay} onClick={onClose}>
      <div
        ref={modalRef}
        className={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div ref={contentRef} className={styles.modalBody}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
